import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Call or text", value: "99 341 431", id: "contact-phone" },
  { icon: Mail, label: "Email", value: "menelaosstokkos8@gmail.com", id: "contact-email" },
  { icon: MapPin, label: "Where", value: "Mobile bar — we come to your venue", id: "contact-location" },
  { icon: Clock, label: "Hours", value: "Events anytime, by booking", id: "contact-hours" },
];

const inputCls =
  "bg-transparent border-foreground/20 rounded-none border-0 border-b px-0 h-11 focus-visible:ring-0 focus-visible:border-matcha placeholder:text-espresso/35";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", event_date: "", guests: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — we will be in touch within a day.");
      setForm({ name: "", email: "", event_date: "", guests: "", message: "" });
    } catch {
      toast.error("Something went wrong — please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-espresso text-alabaster">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-taupe mb-6">Contact us</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6" data-testid="contact-heading">
            Tell us about your <em className="italic text-taupe">gathering</em>
          </h2>
          <p className="text-base leading-relaxed text-alabaster/65 mb-12 max-w-md">
            Dates, headcounts, wild ideas — send them all. We bring our coffee catering to events anywhere in Cyprus and usually reply within one business day with a quote and a menu suggestion.
          </p>
          <ul className="flex flex-col gap-6">
            {INFO.map(({ icon: Icon, label, value, id }) => (
              <li key={id} data-testid={id} className="flex items-start gap-4">
                <span className="mt-0.5 w-9 h-9 rounded-full border border-alabaster/20 flex items-center justify-center shrink-0">
                  <Icon size={15} />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-alabaster/50">{label}</p>
                  <p className="text-sm mt-1">{value}</p>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="lg:col-span-7 bg-alabaster text-espresso rounded-[2rem] p-8 sm:p-12"
        >
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
            <div>
              <Label htmlFor="name" className="font-mono text-[10px] uppercase tracking-widest text-espresso/60">Your name</Label>
              <Input id="name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Jordan Miller" className={inputCls} />
            </div>
            <div>
              <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-widest text-espresso/60">Email</Label>
              <Input id="email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
            </div>
            <div>
              <Label htmlFor="event_date" className="font-mono text-[10px] uppercase tracking-widest text-espresso/60">Event date</Label>
              <Input id="event_date" type="date" data-testid="contact-date-input" value={form.event_date} onChange={set("event_date")} className={inputCls} />
            </div>
            <div>
              <Label htmlFor="guests" className="font-mono text-[10px] uppercase tracking-widest text-espresso/60">Guests (approx.)</Label>
              <Input id="guests" data-testid="contact-guests-input" value={form.guests} onChange={set("guests")} placeholder="e.g. 80" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="message" className="font-mono text-[10px] uppercase tracking-widest text-espresso/60">About your event</Label>
              <Textarea id="message" data-testid="contact-message-input" required value={form.message} onChange={set("message")}
                placeholder="A garden wedding in October, coffee after dinner for around 80 guests..."
                className="bg-transparent border-foreground/20 rounded-none border-0 border-b px-0 min-h-[110px] focus-visible:ring-0 focus-visible:border-matcha placeholder:text-espresso/35 resize-none" />
            </div>
          </div>
          <Button type="submit" data-testid="contact-submit-button" disabled={sending}
            className="mt-10 rounded-full bg-espresso text-alabaster hover:bg-matcha px-10 py-6 font-mono text-[11px] uppercase tracking-widest transition-colors duration-300">
            {sending ? "Sending..." : "Send enquiry"}
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
